import Modal from '@orn/shared-web/components/Modal/Modal';
import * as React from 'react';

import Button from '../Button';

/**
 * Shared confirmation dialog for high-blast-radius admin actions.
 * Domain-agnostic: any caller that needs friction proportional to the
 * blast radius of an action can use this dialog. Today the Feature
 * Gates admin uses it for disable + delete flows, and the Releases
 * admin uses it for both publish (typed) and discard (untyped).
 *
 * Two friction modes, controlled by `confirmValue`:
 *
 * - **Typed mode** (`confirmValue` is a non-empty string): the operator
 *   must type the value verbatim before the destructive button enables.
 *   Case-sensitive on purpose. Use for actions whose blast radius
 *   spans every user (publishing a release, deleting a feature gate).
 *
 * - **Click-only mode** (`confirmValue` omitted or empty): a single
 *   click on the destructive button confirms. Use for reversible or
 *   low-blast-radius actions where typing is overkill (discarding a
 *   release draft — recovery is trivial; the channel just gets a new
 *   announce).
 *
 * Loading and error state are owned here, not by the parent: while
 * `onConfirm`'s promise is in flight the input goes read-only and the
 * action button shows a spinner; if it rejects, the dialog stays open
 * and renders the error message inline. The parent only sees the
 * resolved/rejected promise.
 */

interface IDangerConfirmDialogProps {
  /** Controls modal visibility. Parent owns the open state. */
  isOpen: boolean;
  /** Forwarded to react-aria's overlay so Esc / outside-click flow back. */
  onOpenChange: (isOpen: boolean) => void;
  /**
   * When supplied, the operator must type this value verbatim to enable
   * the destructive button (typed mode). When omitted or an empty
   * string, the dialog renders in click-only mode — a single click on
   * the destructive button confirms. Case-sensitive equality.
   */
  confirmValue?: string;
  /** Header text — e.g. "Disable feature gate" or "Delete feature gate". */
  title: string;
  /** Body of the dialog — explains the consequences. Can include emphasis. */
  body: React.ReactNode;
  /** Label on the destructive submit button — e.g. "Disable" / "Delete". */
  confirmLabel: string;
  /** Async handler. The dialog awaits the promise; rejection keeps it open. */
  onConfirm: () => Promise<void>;
}

/**
 * Renders the dialog. The submit button is disabled until the typed
 * input string-equals `confirmValue`; on submit the dialog awaits
 * `onConfirm` and closes itself on success. On rejection it stays open
 * with the typed input intact so the operator can retry without
 * re-typing.
 */
const DangerConfirmDialog = ({
  isOpen,
  onOpenChange,
  confirmValue,
  title,
  body,
  confirmLabel,
  onConfirm,
}: IDangerConfirmDialogProps) => {
  const [typed, setTyped] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const inputId = React.useId();

  // Reset the typed input every time the dialog opens fresh. Keeps
  // a previous attempt from carrying over after Cancel + reopen.
  React.useEffect(() => {
    if (isOpen) {
      setTyped('');
      setIsSubmitting(false);
      setError(null);
    }
  }, [isOpen]);

  const requiresTyping = Boolean(confirmValue);
  const isMatch = !requiresTyping || typed === confirmValue;
  const canSubmit = isMatch && !isSubmitting;

  const handleConfirm = async () => {
    if (!canSubmit) {
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (err) {
      // Surface the failure inline and keep the dialog open with the typed
      // value intact so the operator can read the reason and retry without
      // re-typing. The Apollo errorLink still reports it to Sentry.
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="sm">
      <div className="flex flex-col">
        <div className="border-fg-error-primary bg-bg-error-primary/10 border-b px-4 py-3">
          <span className="text-fg-error-primary text-xs font-semibold uppercase tracking-widest">{title}</span>
        </div>
        <div className="flex flex-col gap-3 px-4 py-4">
          <div className="text-fg-primary text-sm leading-relaxed">{body}</div>

          {requiresTyping && (
            <div className="flex flex-col gap-1">
              <label htmlFor={inputId} className="text-fg-secondary text-xs font-medium uppercase tracking-wider">
                Type <span className="text-fg-primary font-mono normal-case tracking-normal">{confirmValue}</span> to
                confirm
              </label>
              <input
                id={inputId}
                type="text"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canSubmit) {
                    void handleConfirm();
                  }
                }}
                readOnly={isSubmitting}
                className="border-border-primary bg-bg-primary text-fg-primary placeholder:text-fg-quaternary focus-visible:outline-border-brand w-full border px-2.5 py-1.5 font-mono text-sm focus-visible:outline-2 focus-visible:-outline-offset-1"
                placeholder={confirmValue}
              />
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="border-fg-error-primary bg-bg-error-primary/10 text-fg-error-primary border px-2.5 py-2 text-xs"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button color="secondary" size="sm" onClick={() => onOpenChange(false)} isDisabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              color="danger"
              size="sm"
              onClick={() => void handleConfirm()}
              isDisabled={!canSubmit}
              isLoading={isSubmitting}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DangerConfirmDialog;
